"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ P-1 — project membership is an authorization source, and it runs BOTH ways. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `decisions.md` P-1, settled with the product owner:
 *
 *  * being added to a project **grants the right to log time against its tasks**, whether or not
 *    you are assigned to any of them. So membership is an explicit table the permission check
 *    reads, not a list derived from task assignment;
 *  * ⚠️ **and the rule runs the other way too: you cannot assign a task to somebody who is not a
 *    member of its project.** The owner's exact words when asked: *"No, assigning needs the user
 *    to be added to the project first."*
 *
 * ⚠️ **This is NOT a scope, which is why it lives in its own module.** `none/own/workspace`
 * answers "how far does this grant reach"; membership answers "is this particular person attached
 * to this particular project". A member with `worklog.create` at `workspace` scope still may not
 * log against a project they are not on. Both checks run, and neither substitutes for the other.
 *
 * ⚠️ **Membership is a VALIDITY WINDOW, not a flag.** The mockup already anticipated this —
 * `personProjects()` returns `{ from, until, active }` per membership — so somebody who left a
 * project in March cannot log time against it in August, and their March worklogs stay.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.wasMemberOn = wasMemberOn;
exports.mayLogTime = mayLogTime;
exports.mayAssign = mayAssign;
exports.mayRemoveFromProject = mayRemoveFromProject;
/**
 * Was this member on the project on `on`?
 *
 * ⚠️ **String comparison, and it is correct only because these are wire dates.** `yyyy-mm-dd` is
 * lexicographically ordered, so `'2026-03-01' <= '2026-08-24'` is a real date comparison. It is
 * wrong for every other date format, which is one more reason nothing but the wire form crosses
 * a boundary in this codebase.
 */
function wasMemberOn(memberships, projectId, memberId, on) {
    return memberships.some((membership) => membership.projectId === projectId &&
        membership.memberId === memberId &&
        membership.from <= on &&
        (membership.until === null || on <= membership.until));
}
/**
 * May this member log time against a task in this project, on this date?
 *
 * ⚠️ **The date matters and it is the task's WORK date, not today.** Logging on Monday for work
 * done last Wednesday is ordinary, and the question is whether they were on the project *then*.
 * Using `today` here would refuse a legitimate catch-up entry and allow a back-dated one after
 * they left — both wrong, in opposite directions.
 *
 * `outside_membership_window` is distinguished from `not_a_project_member` because the two need
 * different sentences: one is "ask to be added", the other is "you left this project in March".
 */
function mayLogTime(input) {
    if (!input.hasWorklogCreate)
        return { ok: false, reason: 'not_permitted' };
    /* ⚠️ **A task with NO project has no membership constraint**, and P-1 says so explicitly: *"the
     * rule is conditional on the task having one"*. Refusing here would make an unfiled task
     * unloggable, which is not what anybody decided. */
    if (input.projectId === null)
        return { ok: true };
    const onProject = input.memberships.some((membership) => membership.projectId === input.projectId && membership.memberId === input.memberId);
    if (!onProject)
        return { ok: false, reason: 'not_a_project_member' };
    if (!wasMemberOn(input.memberships, input.projectId, input.memberId, input.workedOn)) {
        return { ok: false, reason: 'outside_membership_window' };
    }
    return { ok: true };
}
/**
 * May this member assign that task to that person?
 *
 * ⚠️ **The membership checked is the ASSIGNEE's, not the actor's.** That inversion is the whole
 * content of P-1's second half and the easiest thing to get backwards: a project manager with
 * `task.assign` at workspace scope may still not assign work to somebody who is not on the
 * project. P-1 lists four consequences that all reduce to this one call — creating a task with an
 * assignee, **moving a task between projects** (check the assignee against the TARGET project),
 * "Rám osztás" from the row menu, and the bulk version of it.
 *
 * ⚠️ **The assignee picker is scoped to project members, and that is a deliberate divergence from
 * the mockup**, which lists every user in `assigneeItems`. Refusing at submit what the picker
 * offered is worse than not offering it.
 */
function mayAssign(input) {
    if (!input.hasTaskAssign)
        return { ok: false, reason: 'not_permitted' };
    if (input.projectId === null)
        return { ok: true };
    if (!wasMemberOn(input.memberships, input.projectId, input.assigneeMemberId, input.on)) {
        return { ok: false, reason: 'assignee_not_a_project_member' };
    }
    return { ok: true };
}
/**
 * May this member be removed from the project?
 *
 * ⚠️ **P-1: removing somebody who holds assigned tasks is REFUSED, with the count — not silently
 * unassigned.** *"Silent unassignment loses who was doing the work."* The count is part of the
 * refusal because the design system's voice rule is to say the number: *"Két függőség sérül"*,
 * not "there is a problem".
 */
function mayRemoveFromProject(input) {
    if (input.assignedTaskCount > 0) {
        return { ok: false, reason: 'holds_assigned_tasks', count: input.assignedTaskCount };
    }
    return { ok: true };
}
//# sourceMappingURL=project-membership.js.map